/**
 * Enhanced Trend Indicator Component
 * Story: E007-S014 - Trend Indicators (↑↓→)
 * 
 * Features:
 * - Direction arrows (↑↓→)
 * - Color coding (green/red/gray)
 * - Hover tooltip with details
 * - Mini sparkline chart
 * - Alert badge for >20% declines
 */

import React, { useState, useRef, useEffect } from 'react';
import { formatTrendDisplay, getTrendColor, getTrendBgColor, TrendData } from '../lib/trendsApi';

interface TrendIndicatorEnhancedProps {
  trend: TrendData;
  metricLabel?: string;
  metricFormat?: 'number' | 'currency' | 'percentage' | 'multiplier';
  showSparkline?: boolean;
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

// Mini Sparkline Component
const TrendSparkline: React.FC<{ values: number[]; direction: string }> = ({ values, direction }) => {
  if (!values || values.length < 2) return null;

  const width = 60;
  const height = 20;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  // Normalize values to fit in chart
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  });

  const pathD = `M ${points.join(' L ')}`;
  const strokeColor = direction === 'up' ? '#10B981' : direction === 'down' ? '#EF4444' : '#6B7280';

  return (
    <svg width={width} height={height} className="inline-block ml-2">
      <path
        d={pathD}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Tooltip Component
const TrendTooltip: React.FC<{
  trend: TrendData;
  metricLabel: string;
  metricFormat: string;
  visible: boolean;
  position: { x: number; y: number };
}> = ({ trend, metricLabel, metricFormat, visible, position }) => {
  if (!visible) return null;

  const formatValue = (value: number) => {
    switch (metricFormat) {
      case 'currency':
        return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'percentage':
        return `${value.toFixed(2)}%`;
      case 'multiplier':
        return `${value.toFixed(2)}x`;
      default:
        return value.toLocaleString();
    }
  };

  const changeAbsolute = trend.current_value - trend.previous_value;
  const changeSign = changeAbsolute >= 0 ? '+' : '';

  return (
    <div
      className="absolute z-50 bg-white dark:bg-boxdark rounded-lg shadow-lg border border-stroke dark:border-strokedark p-3 min-w-[200px] transition-opacity duration-150"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -100%)',
        marginTop: '-8px',
      }}
    >
      {/* Header */}
      <div className="text-sm font-semibold text-black dark:text-white mb-2">
        {metricLabel} Trend
      </div>

      {/* Comparison period */}
      <div className="text-xs text-bodydark dark:text-bodydark1 mb-2">
        vs last 7 days
      </div>

      {/* Values */}
      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-bodydark dark:text-bodydark1">Previous:</span>
          <span className="font-medium text-black dark:text-white">
            {formatValue(trend.previous_value)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-bodydark dark:text-bodydark1">Current:</span>
          <span className="font-medium text-black dark:text-white">
            {formatValue(trend.current_value)}
          </span>
        </div>
        <div className="flex justify-between border-t border-stroke dark:border-strokedark pt-1 mt-1">
          <span className="text-bodydark dark:text-bodydark1">Change:</span>
          <span className={`font-medium ${getTrendColor(trend.direction)}`}>
            {changeSign}{formatValue(changeAbsolute)} ({changeSign}{trend.change_percent.toFixed(1)}%)
          </span>
        </div>
      </div>

      {/* Mini Sparkline */}
      {trend.daily_values && trend.daily_values.length > 1 && (
        <div className="mt-2 pt-2 border-t border-stroke dark:border-strokedark">
          <div className="text-xs text-bodydark dark:text-bodydark1 mb-1">Last 7 days:</div>
          <TrendSparkline values={trend.daily_values} direction={trend.direction} />
        </div>
      )}

      {/* Alert warning */}
      {trend.is_alert && (
        <div className="mt-2 p-2 bg-danger/10 rounded text-danger text-xs flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>Sharp decline detected</span>
        </div>
      )}

      {/* Arrow pointer */}
      <div
        className="absolute left-1/2 -bottom-2 transform -translate-x-1/2 border-8 border-transparent border-t-white dark:border-t-boxdark"
      />
    </div>
  );
};

export const TrendIndicatorEnhanced: React.FC<TrendIndicatorEnhancedProps> = ({
  trend,
  metricLabel = 'Metric',
  metricFormat = 'number',
  showSparkline = true,
  showTooltip = true,
  size = 'md',
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!showTooltip) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPosition({
        x: rect.width / 2,
        y: 0,
      });
    }
    setTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
  };

  // Size classes
  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  // Get arrow
  const getArrow = () => {
    switch (trend.direction) {
      case 'up':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'down':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const formattedTrend = formatTrendDisplay(trend.direction, trend.change_percent);
  const colorClass = getTrendColor(trend.direction);
  const bgClass = getTrendBgColor(trend.direction);

  return (
    <div
      ref={containerRef}
      className={`relative inline-flex items-center gap-1 px-2 py-1 rounded ${bgClass} ${colorClass} ${sizeClasses[size]} font-medium cursor-default transition-all duration-150`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Arrow */}
      {getArrow()}

      {/* Percentage */}
      <span>
        {trend.change_percent >= 0 ? '+' : ''}{trend.change_percent.toFixed(1)}%
      </span>

      {/* Alert icon */}
      {trend.is_alert && (
        <svg className="w-4 h-4 text-danger animate-pulse" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      )}

      {/* Inline sparkline (optional) */}
      {showSparkline && trend.daily_values && trend.daily_values.length > 1 && (
        <TrendSparkline values={trend.daily_values} direction={trend.direction} />
      )}

      {/* Tooltip */}
      {showTooltip && (
        <TrendTooltip
          trend={trend}
          metricLabel={metricLabel}
          metricFormat={metricFormat}
          visible={tooltipVisible}
          position={tooltipPosition}
        />
      )}
    </div>
  );
};

export default TrendIndicatorEnhanced;
